import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { skipToken, useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { TFunction } from 'i18next';

import { checkoutAPIService } from '~/api/checkout';
import { statsAPIService } from '~/api/stats';
import Card1 from '~/assets/icons/analytics/card1.svg?react';
import Card2 from '~/assets/icons/analytics/card2.svg?react';
import Card3 from '~/assets/icons/analytics/card3.svg?react';
import Card4 from '~/assets/icons/analytics/card4.svg?react';
import EmptyGraph from '~/assets/images/graphEmpty.png';
import { Text } from '~/components/ui/text';
import { IS_ACTIVE_SUBSCRIPTION_STATUS } from '~/constants/common';
import { checkoutKeys, statsKeys } from '~/constants/queryKeys';
import { handlePeriodSelection } from '~/pages/Analytics/utils';
import { COLORS } from '~/styles/colors';

import { AnalyticsCard } from './components/AnalyticsCard/AnalyticsCard';
import { PieChartCard } from './components/CircleCard/PieChartCard';
import { CustomSelect } from './components/CustomSelect/CustomSelect';
import { DateRangePicker } from './components/DateRangePicker/DateRangePicker';
import { MultiLineStatsChart } from './components/MultiLineStatsChart/MultiLineStatsChart';
import { useStyles } from './analytics.styles';

// Utility functions for options
const getPeriodOptions = (t: TFunction) => [
  { label: t('analytics:periodOptions.today'), value: 'today' },
  { label: t('analytics:periodOptions.yesterday'), value: 'yesterday' },
  { label: t('analytics:periodOptions.last7days'), value: 'last7days' },
  { label: t('analytics:periodOptions.last30days'), value: 'last30days' },
  { label: t('analytics:periodOptions.last90days'), value: 'last90days' },
  { label: t('analytics:periodOptions.lifetime'), value: 'lifetime' },
];

export function Analytics() {
  const styles = useStyles();
  const { t } = useTranslation();
  const today = new Date();
  const [range, setRange] = useState({ from: subDays(today, 6), to: today });
  const [params, setParams] = useState({
    grouping: 'day',
    period: 'last7days',
    qr_code: [],
    operating_system: [],
    countries: [],
    cities: [],
  });

  const queryParams = {
    grouping: params.grouping,
    scanned_at__gte: range.from ? format(range.from, 'yyyy-MM-dd') : null,
    scanned_at__lt: range.to ? format(range.to, 'yyyy-MM-dd') : null,
    city: params.cities,
    country: params.countries,
    operating_system: params.operating_system,
    qr_code__id: params.qr_code,
  };

  // const allOption = [{ label: t('analytics:qrCodeOptions.all'), value: 'all' }];

  const { data: hasActiveSubscription } = useQuery({
    queryKey: [checkoutKeys.subscription],
    queryFn: () => checkoutAPIService.checkSubscription(),
    select: (response) => response.data.status === IS_ACTIVE_SUBSCRIPTION_STATUS,
  });

  const {
    data: stats,
    isFetching: isFetchingStats,
    isLoading: isLoadingStats,
  } = useQuery({
    queryKey: statsKeys.full({ range, ...params }),
    queryFn: hasActiveSubscription
      ? () => {
          return statsAPIService.getStatsQrCodes(queryParams);
        }
      : skipToken,
    select: (response) => {
      return {
        statistic: response.data.statistic,
        dateFromRaw: response.data.dateFromRaw,
        dateToRaw: response.data.dateToRaw,
        diagramV2: response.data.diagramV2,
        operatingSystemOptions: response.data.filtersAll.operatingSystems.map((item) => {
          return {
            label: item,
            value: item,
          };
        }),
        qrCodesOptions: response.data.filtersAll.qrCodes.map((item) => {
          return {
            label: item.name ?? 'N/A',
            value: String(item.id),
          };
        }),
        citiesOptions: response.data.filtersAll.cities.map((item) => {
          const [[id, label]] = Object.entries(item);
          return {
            label: label ?? 'N/A',
            value: String(id),
          };
        }),
        countriesOptions: response.data.filtersAll.countries.map((item) => {
          const [[id, label]] = Object.entries(item);
          return {
            label: label ?? 'N/A',
            value: String(id),
          };
        }),
      };
    },

    placeholderData: (prev) => prev,
  });

  const periodOptions = getPeriodOptions(t);
  const {
    data: scansByParamsFull,
    isFetching: isFetchingScansByParamsFull,
    isLoading: isLoadingScansByParamsFull,
  } = useQuery({
    queryKey: [...statsKeys.scansBy(), range, params], // TODO remove country from params
    queryFn: hasActiveSubscription
      ? () => {
          return statsAPIService.getStatsQrByPramsFull(queryParams);
        }
      : skipToken,
    select: (response) => {
      return {
        byOperatingSystem: response.data.byOperatingSystem.map((item) => {
          return {
            label: item.operatingSystem ?? 'N/A',
            value: item.percentage,
          };
        }),
        byCountry: response.data.byCountry.map((item) => {
          return {
            label: item.countryName ?? 'N/A',
            value: item.percentage,
          };
        }),
        byCity: response.data.byCity.map((item) => {
          return {
            label: item.cityName ?? 'N/A',
            value: item.percentage,
          };
        }),
      };
    },
    placeholderData: (prev) => prev,
  });

  return (
    <Box sx={styles.analyticContainer}>
      {/* Header Section */}
      <Box sx={styles.analyticHeader}>
        <Text variant="h2">{t('analytics:title')}</Text>
      </Box>

      {/* Filters Section */}
      <Box sx={styles.filterContainer}>
        <CustomSelect
          title={t('analytics:period')}
          value={params.period}
          onChange={(val: string) => {
            const newRange = handlePeriodSelection(val);
            setRange(newRange);
            setParams((prev) => ({ ...prev, period: val as string }));
          }}
          options={periodOptions}
          placeholder={t('analytics:selectPeriod')}
        />
        <DateRangePicker
          maxDate={new Date()}
          setRange={(range) => setRange(range ? range : { from: null, to: null })}
          range={range}
        />
        <CustomSelect
          title={t('analytics:qrCode')}
          value={params.qr_code}
          onChange={(val) => setParams((prev) => ({ ...prev, qr_code: val as string[] }))}
          options={stats?.qrCodesOptions ?? []}
          multiple
          placeholder={t('analytics:selectQrCode')}
        />
        <CustomSelect
          title={t('analytics:operatingSystem')}
          value={params.operating_system}
          onChange={(val) => setParams((prev) => ({ ...prev, operating_system: val as string[] }))}
          options={stats?.operatingSystemOptions ?? []}
          multiple
          placeholder={t('analytics:selectOperatingSystem')}
        />
        <CustomSelect
          title={t('analytics:countries')}
          value={params.countries}
          onChange={(val) => setParams((prev) => ({ ...prev, countries: val as string[] }))}
          options={stats?.countriesOptions ?? []}
          multiple
          placeholder={t('analytics:selectCountries')}
        />
        <CustomSelect
          title={t('analytics:cities')}
          value={params.cities}
          onChange={(val) => setParams((prev) => ({ ...prev, cities: val as string[] }))}
          searchable
          options={stats?.citiesOptions ?? []}
          multiple
          placeholder={t('analytics:selectCities')}
        />
      </Box>

      {/* Cards Section */}
      <Box sx={styles.cardsContainer}>
        <AnalyticsCard
          text={t('analytics:totalQrCodes')}
          value={stats?.statistic.totalQrCount}
          icon={<Card1 />}
          isLoading={isFetchingStats}
        />
        <AnalyticsCard
          text={t('analytics:totalScans')}
          value={stats?.statistic.totalQrScans}
          icon={<Card2 />}
          isLoading={isFetchingStats}
        />
        <AnalyticsCard
          text={t('analytics:totalUniqueScans')}
          value={stats?.statistic.totalQrUniqueScans}
          icon={<Card3 />}
          tooltip={t('analytics:totalUniqueScansTooltip')}
          isLoading={isFetchingStats}
        />
        <AnalyticsCard
          text={t('analytics:totalVisits')}
          value={stats?.statistic.totalQrVisits}
          icon={<Card4 />}
          isLoading={isFetchingStats}
        />
      </Box>

      {/* Graph Section */}
      <Box sx={{ position: 'relative' }}>
        {isFetchingStats && !isLoadingStats && <CircularProgress sx={styles.fetchingIndicator} size={60} />}
        <Box sx={styles.mainGraphContainer(isFetchingStats)}>
          {/* Graph Header */}
          <Box sx={styles.mainGraphHeader}>
            <Text variant="h3">{t('analytics:qrCodeScanActivities')}</Text>
            <Box sx={styles.dateContainer}>
              <Text variant="button14Med">{t('analytics:analyzedPeriod')}</Text>
              {stats?.dateFromRaw && (
                <Text variant="button14Med" sx={{ color: COLORS.grey400 }}>
                  {format(new Date(stats.dateFromRaw), 'MMM d, yyyy')} -{' '}
                  {format(new Date(stats.dateToRaw), 'MMM d, yyyy')}
                </Text>
              )}
            </Box>
          </Box>
          {/* Graph Content */}
          {isLoadingStats ? (
            <Box sx={styles.loadingGraphWrapper}>
              <CircularProgress size={60} />
            </Box>
          ) : (
            <AnalyticsGraphContent
              diagramV2={stats?.diagramV2}
              grouping={params.grouping}
              onChangeGrouping={(newGrouping) =>
                setParams((prevState) => ({
                  ...prevState,
                  grouping: newGrouping,
                }))
              }
              styles={styles}
            />
          )}
        </Box>
      </Box>

      {/* Circular Cards Section */}
      <Box sx={styles.circlesCardsContainer}>
        <PieChartCard
          title={t('analytics:scansByOperatingSystems')}
          isLoading={isLoadingScansByParamsFull}
          isFetching={isFetchingScansByParamsFull}
          data={scansByParamsFull?.byOperatingSystem ?? []}
        />
        <PieChartCard
          title={t('analytics:scansByCountry')}
          tooltip={t('analytics:tooltipCountry')}
          isLoading={isLoadingScansByParamsFull}
          isFetching={isFetchingScansByParamsFull}
          data={scansByParamsFull?.byCountry ?? []}
        />
        <PieChartCard
          title={t('analytics:scansByRegionCity')}
          tooltip={t('analytics:tooltipCity')}
          isLoading={isLoadingScansByParamsFull}
          isFetching={isFetchingScansByParamsFull}
          data={scansByParamsFull?.byCity ?? []}
        />
      </Box>

      {/* Footer Section */}
      <Text variant="inputVerySmall" sx={{ color: '#6D6D6D', textAlign: 'center' }}>
        {t('analytics:reportsNotRealTime')}
      </Text>
    </Box>
  );
}

// New component: AnalyticsGraphContent
type AnalyticsGraphContentProps = {
  diagramV2: any; // Define more precise typing based on the actual structure
  grouping: string; // Current grouping value
  onChangeGrouping: (grouping: string) => void; // Callback to change grouping
  styles: any; // Styles object
};

const AnalyticsGraphContent = ({ diagramV2, grouping, onChangeGrouping, styles }: AnalyticsGraphContentProps) => {
  const { t } = useTranslation();
  return (
    <>
      {/* Graph Content */}
      {diagramV2?.length ? (
        <MultiLineStatsChart datesData={diagramV2} grouping={grouping} onChangeGrouping={onChangeGrouping} />
      ) : (
        <Box sx={styles.mainGraphEmpty}>
          <Box component="img" src={EmptyGraph} alt="Empty graph" />
          <Text variant="input15" sx={{ color: '#6D6D6D' }}>
            {t('analytics:noDataStatistics')}
          </Text>
        </Box>
      )}
    </>
  );
};
